export interface ICoastalResponseMessages {
  showThinningCoastalMessage: boolean;
  showThinningNonCoastalMessage: boolean;
  showFirebreakCoastalMessage: boolean;
}

export interface ILotPlanModel {
  lot: string;
  plan: string;
  lotOnPlanValid: boolean;
  noRegionalEcosystemsFound: boolean;
  area: number;
  categories?: string[] | null;
  localGovernmentAreas?: string[] | null;
  canContinue: boolean;
  hasValidCategories: boolean;
  tenure?: string | null;
  tenureIsValid: boolean;
  bioregions?: string[] | null;
  containsForestConsentArea: boolean;
  containsForestEntitlementArea: boolean;
  containsKoalaHabitatArea: boolean;
  containsKoalaPriorityArea: boolean;
  containsLocallyRefinedKoalaHabitatArea: boolean;
  containsSeqRegionalPlanArea: boolean;
  containsWetTropicsQldWorldHeritageArea: boolean;
  clearingInAForestAreaValid: boolean;
  isCoastalLotPlanBioregion: boolean;
}

export interface ILandholderConfirmationModel {
  areYouTheLandholder?: boolean;
  areYouAuthorised?: boolean;
  areYouLandsOfficer?: boolean;
}

export interface IAcknowledgementNotificationDataModel {
  notificationReferenceNumber: string | null;
}

export interface IAcknowledgementsWrapperModel {
  sessionKey: string;
  acknowledgements: IAcknowledgementData[];
  questions: IAcknowledgementQuestion[];
}

export interface IAcknowledgementData {
  message: string;
  response: boolean | undefined;
}

export interface IAcknowledgementQuestion {
  acknowledgementQuestionId?: number;
  description?: string | null;
  response?: boolean;
}

export interface IAcknowledgementsDataModel {
  clearingInAForestArea: IAcknowledgementData | null;
  koalaHabitatProtection: IAcknowledgementData | null;
  wetTropicsQldWorldHeritageArea: IAcknowledgementData | null;
  stateCommercialTimberRights: IAcknowledgementData | null;
  recordKeepingAcknowledgementAccepted: boolean;
  questions: IAcknowledgementQuestionModel[];
  acknowledgements: string[];
}

export interface IAcknowledgementQuestionModel {
  acknowledgementQuestionId: number;
  description: string;
}

export interface ILandholderDetailsModel {
  mailingAddressSameAsPhysicalAddress?: boolean;
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  companyName?: string | null;
  phone?: string | null;
  mobile?: string | null;
  email?: string;
  natureOfConsent?: string | null;
  username?: string | null;
  location?: string | null;
  canAttachEvidenceOfConsent?: boolean;
  evidenceOfConsentFileNames?: string[];
  homeAddress: IAddressModel;
  mailingAddress: IAddressModel;
}

export interface INotifierDetailsModel {
  mailingAddressSameAsPhysicalAddress?: boolean;
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  companyName?: string | null;
  phone?: string | null;
  mobile?: string | null;
  email?: string;
  natureOfConsent?: string | null;
  username?: string | null;
  location?: string | null;
  canAttachEvidenceOfConsent?: boolean;
  evidenceOfConsentProvided?: boolean;
  evidenceOfConsentFileNames?: string[];
  homeAddress: IAddressModel;
  mailingAddress: IAddressModel;
}

export interface IReferenceDataModel {
  id: number;
  code: string;
  name: string;
}

export interface IReasonModel {
  code: string;
  text?: string;
}

interface IAddressModel {
  line1: string;
  line2: string;
  suburb: string;
  postcode: string;
  stateId: number;
  countryId: number;
}

export interface IGetLotOnPlanPurposeModel {
  purposes: IPurposeModel[];
}

export interface IPurposeModel {
  purposeId: number;
  purposeCode: string;
  purposeDescription: string;
}

export interface IPurposeDataModel {
  purposeMessageAnswerValid: boolean | null;
  purpose: IPurposeModel;
}

export interface IGeneralInformationModel {
  promptQuestionMessage: string;
  userSelectsNoDisplayMessage: string;
  userSelectsYesDisplayMessage: string;
  generalInfoMessage: string;
  secondPromptQuestionMessage: string;
  secondUserSelectsNoDisplayMessage: string;
  secondUserSelectsYesDisplayMessage: string;
}

export interface ICategoryPurposeDataModel {
  allowsCreatingNewNotification: boolean;
  allowMultipleActivities: boolean;
  areRegionalEcosystemsRequired: boolean;
  categoryPurposeId: number;
  categoryCode: string;
  createNewPreventedReason: string;
  isProposed: boolean;
  maxAllowableClearedAreaHa: number | null;
  maxAllowableClearedAreaHaNonCoastal: number | null;
  maxAllowableClearedAreaHaCoastal: number | null;
  previousCategoryPurpose: number | null;
  purposeCode: string;
  purposeDescription: string;
  requiresReason: boolean;
  requiresActivities: boolean;
  requiresIsCoastalResponse: boolean;
  requiresRecordKeepingAcknowledgements: boolean;
  requiresAtLeastOneRecordKeepingAck: boolean;
  requiresMeasuredDensity: boolean;
  showsPreviousNotifications: boolean;
  showsPreviousThinningNotifications: boolean;
  showRecordKeepingAcknowledgements: boolean;
  usesRegionalEcosystemStatus: boolean;
  withinCorrectLocalGovernmentAreas: boolean;
}

export interface ICategoryPurposeActivityDataModel {
  activityCode: string;
  activityId: number;
  areRegionalEcosystemsRequired: boolean;
  categoryPurposeId: number;
  description: string;
  estimatedAreaOptional: boolean;
  gpsPointsOptional: boolean;
  maximumAreaOverride: number | null;
  maximumNumberOfGPSPointsOverride: number | null;
  minimumNumberOfGPSPointsOverride: number | null;
  requiresAreaInHectares: boolean;
  requiresChannelDimensions: boolean;
  requiresExchangeArea: boolean;
  requiresNotificationArea: boolean;
  requiresReason: boolean;
  usesRegionalEcosystemStatus: boolean;
}

export interface IDeclarationsDataModel {
  acceptedDeclaration: boolean,
  acceptedPrivacyStatement: boolean
}

export interface ICategoryPurposeRegionalEcosystemStatusModel {
  code: string;
  message: string;
  regionalEcosystemStatusId: number;
  categoryPurposeId: number;
  category: string;
  blockNotificationIfSelected: boolean;
  sequence: number;
}

export interface ISelfAuditDataModel {
  hasAuditEvidence: boolean | null;
}

export interface IExchangeAreaDataModel {
  userOverrideIsExchangeAreaRequired: boolean | null;
  exchangeAreaFileReferenceNumber: string;
}

export interface IExchangeAreaInformation {
  header: string;
  questions: string[];
}

export interface ISelectCategoryModel {
  selectedCategories: [];
  availableCategoryPurposes: ICategoryPurposeDataModel[];
}

export interface IPreviousNotificationModel {
  title: string;
  areaAmount: number;
  percentCleared: number;
  startDate: Date | string;
  endDate: Date | string | null;
}

export interface INotificationsForLotPlanModel {
  notifications: IPreviousNotificationModel[];
}

export interface INotificationAreaLimitModel {
  remainingAreaLimitInHectares: number;
  maxAreaLimitInHectares: number;
}

export interface IActivityStorageDataModel {
  activity: string;
  hasMeasuredDensity: boolean | null;
  selectedActivities: ICategoryPurposeActivityDataModel[] | null;
}

export interface IActivityDataModel {
  activityId: number;
  activityCode: string;
  description: string;
  categoryPurposeId: number;
}

export interface IActivityRegionalEcosystemModel {
  description: string;
  selectedRegionalEcosystems: IReviewNotificationRegionalEcosystemData[];
}

export interface IReviewNotificationRegionalEcosystemData {
  regionalEcosystem: string;
  addedManuallyFromSearchResult: boolean;
}

export interface IRegionalEcosystem {
  regionalEcosystemId: number;
  regionalEcosystem?: string | null;
  categoryPurposeId: number;
  activityCode?: string | null;
  message?: string | null;
  activityId?: number | null;
}

export interface IRegionalEcosystemSearchResultsModel {
  categoryPurposeId: number;
  activityId: number;
  activityCode: string;
  regionalEcosystems: IRegionalEcosystem[];
}

export interface IRegionalEcosystemDataModel {
  regionalEcosystemsApplicableFromLotPlan: IRegionalEcosystem[];
  regionalEcosystemsAddedFromSearchResults: IRegionalEcosystem[];
  regionalEcosystems: IRegionalEcosystem[];
}

export interface ICategoryGroup {
  categoryCode: string;
  categoryPurposeId: number;
  activityGroups: IActivityGroup[];
}

export interface IActivityGroup {
  activityId?: number | null;
  activityCode?: string | null;
  activityDescription?: string | null;
  availableRegionalEcosystems?: IRegionalEcosystem[] | null;
  selectedRegionalEcosystems?: IRegionalEcosystem[] | null;
  regionalEcosystemsAddedFromSearchResults?: IRegionalEcosystem[] | null;
  regionalEcosystemsApplicableFromLotPlan?: IRegionalEcosystem[] | null;
  categoryPurposeId: number;
  areRegionalEcosystemsRequired?: boolean | null;
}

export interface IRegionalEcosystemStatusResponseModel {
  messages: string[];
  selectedStatusGroups: ISelectedStatusGroupModel[];
}

export interface IStatusGroupStatusModel {
  blockNotificationIfSelected: boolean;
  category: string;
  categoryPurposeId: number;
  code: string;
  message: string;
  regionalEcosystemStatusId: number;
  sequence: number;
}

export interface ISelectedStatusGroupModel {
  category: string;
  statuses: ICategoryPurposeRegionalEcosystemStatusModel[];
}

export interface INotificationAreaModel {
  howToProvide: string;
  coordinates: string[];
  lodgementShapeGeoJson: string;
  lotPlanShapeGeoJson: string;
  isInRestrictedClearingArea: boolean;
  isCoastalResponse: boolean;
  area: number;
  isHarvestedWithinTenYears: boolean;
  mapProvided: boolean;
  mapFileNames: string[];
  isKoalaLotPlanArea: boolean;
  areaWidth: number;
  lineLength: number;
}

export interface ISetActivitiesResponseModel {
  isExchangeAreaRequired: boolean;
}

export interface ISetAreaResponseModel {
  isExchangeAreaRequired: boolean;
}

export interface IRegionalEcosystemStatusesResponseModel {
  isExchangeAreaRequired: boolean;
}

export interface IRegionalEcosystemStatusModel {
  regionalEcosystemStatusIds: number[];
  messages: string[];
  selectedStatusGroups: ISelectedStatusGroupModel[];
}

export interface IChannelDimensionsDataModel {
  sessionKey: string;
  width: number;
  length: number;
}

export interface IChannelDimensionsLimitsResponseModel {
  maxWidth: number;
  maxLength: number;
}
export interface IChannelDimensionsModel {
  width: number;
  exchangeAreaRequired: boolean;
}

export interface SpatialConfigModel {
  AddressTypeAheadURL: string;
  AddressURL: string;
  LotplanMapURL: string;
  LotplanbupURL: string;
}

export interface ISpatialMapLayerData {
  id: string;
  layer: any;
}

export interface ILotPlanRegionalEcosystemWithMetadata {
  reLabel: string;
  re1: string;
  re2: string;
  ruleId: string;
  percent: string;
  vmPoly: string;
  vmStatus: string;
}

export interface IRECategoryGroupingAndGeom {
  reRuleStringId: string;
  reRuleStringForDisplay: string;
  reVmRuleId: string;
  regionalEcosystems: string[];
  spatialMapLayerData: ISpatialMapLayerData;
}

export interface IFinalizeNotificationModel {
  sessionKey: string;
}

export interface IFinalizeNotificationResponseModel {
  notificationReferenceNumber: string;
}

// ************************************
// Implementation Classes goes below
// ************************************

export class LotPlanModel implements ILotPlanModel {
  sessionKey = '';
  lot = '';
  plan = '';
  lotOnPlanValid = false;
  noRegionalEcosystemsFound = false;
  area = 0;
  categories = undefined;
  localGovernmentAreas = undefined;
  canContinue = false;
  hasValidCategories = false;
  tenure = undefined;
  tenureIsValid = false;
  bioregions = undefined;
  containsForestConsentArea = false;
  containsForestEntitlementArea = false;
  containsKoalaHabitatArea = false;
  containsKoalaPriorityArea = false;
  containsLocallyRefinedKoalaHabitatArea = false;
  containsSeqRegionalPlanArea = false;
  containsWetTropicsQldWorldHeritageArea = false;
  clearingInAForestAreaValid = true;
  isCoastalLotPlanBioregion = false;
}

export class LandholderDetailsModel implements ILandholderDetailsModel {
  mailingAddressSameAsPhysicalAddress = false;
  firstName = '';
  middleName = '';
  lastName = '';
  companyName = '';
  phone = '';
  mobile = '';
  email = '';
  natureOfConsent = '';
  username = '';
  location = '';
  canAttachEvidenceOfConsent = false;
  evidenceOfConsentFileNames = [];
  homeAddress = new AddressModel()
  mailingAddress = new AddressModel()
}

export class NotifierDetailsModel implements INotifierDetailsModel {
  mailingAddressSameAsPhysicalAddress = false;
  firstName = '';
  middleName = '';
  lastName = '';
  companyName = '';
  phone = '';
  mobile = '';
  email = '';
  natureOfConsent = '';
  username = '';
  location = '';
  canAttachEvidenceOfConsent = false;
  evidenceOfConsentFileNames: string[] = [];
  homeAddress = new AddressModel()
  mailingAddress = new AddressModel()
}

export class LandholderConfirmationModel implements ILandholderConfirmationModel {
}

export class AcknowledgementQuestionsModel implements IAcknowledgementQuestion {
  acknowledgementQuestionId = 0;
  response = false;
  description = '';
}

export class PurposeDataModel implements IPurposeDataModel {
  purposeMessageAnswerValid = null;
  purpose = {} as IPurposeModel;

}

export class PurposeModel implements IPurposeModel {
  purposeId = 0;
  purposeCode = '';
  purposeDescription = '';
}

export class AddressModel implements IAddressModel {
  line1: string;
  line2: string;
  suburb: string;
  postcode: string;
  stateId: number;
  countryId: number;

  constructor() {
    this.line1 = '';
    this.line2 = '';
    this.suburb = '';
    this.postcode = '';
    this.stateId = 6;
    this.countryId = 0;
  }
}

export class CategoryPurposeDataModel implements ICategoryPurposeDataModel {
  allowsCreatingNewNotification = false;
  allowMultipleActivities = false;
  areRegionalEcosystemsRequired = false;
  categoryPurposeId = 0;
  categoryCode = '';
  createNewPreventedReason = '';
  isProposed = false;
  maxAllowableClearedAreaHa = 0;
  maxAllowableClearedAreaHaNonCoastal = 0;
  maxAllowableClearedAreaHaCoastal = 0;
  previousCategoryPurpose = 0;
  purposeCode = '';
  purposeDescription = '';
  requiresReason = false;
  requiresActivities = false;
  requiresIsCoastalResponse = false;
  requiresRecordKeepingAcknowledgements = false;
  requiresAtLeastOneRecordKeepingAck = false;
  requiresMeasuredDensity = false;
  showsPreviousNotifications = false;
  showsPreviousThinningNotifications = false;
  showRecordKeepingAcknowledgements = false;
  usesRegionalEcosystemStatus = false;
  withinCorrectLocalGovernmentAreas = false;
}

export class ActivityDataModel implements IActivityDataModel {
  activityId = 0;
  activityCode = '';
  description = '';
  categoryPurposeId = 0;
}

export class ActivityStorageDataModel implements IActivityStorageDataModel {
  activity = '';
  hasMeasuredDensity = false;
  selectedActivities = [];

}

export class CategoryPurposeActivityDataModel implements ICategoryPurposeActivityDataModel {
  activityCode = '';
  activityId = 0;
  areRegionalEcosystemsRequired = false;
  categoryPurposeId = 0;
  description = '';
  estimatedAreaOptional = false;
  gpsPointsOptional = false;
  maximumAreaOverride = 0;
  maximumNumberOfGPSPointsOverride = 0;
  minimumNumberOfGPSPointsOverride = 0;
  requiresAreaInHectares = false;
  requiresChannelDimensions = false;
  requiresExchangeArea = false;
  requiresNotificationArea = false;
  requiresReason = false;
  usesRegionalEcosystemStatus = false;
}

export class CategoryPurposeRegionalEcosystemStatusModel implements ICategoryPurposeRegionalEcosystemStatusModel {
  code = '';
  message = '';
  regionalEcosystemStatusId = 0;
  categoryPurposeId = 0;
  category = '';
  blockNotificationIfSelected = false;
  sequence = 0;
}

export class SelectedStatusGroupModel implements ISelectedStatusGroupModel {
  category = ''
  statuses = [] as ICategoryPurposeRegionalEcosystemStatusModel[];
}

export class NotificationAreaLimitModel implements INotificationAreaLimitModel {
  remainingAreaLimitInHectares = 0;
  maxAreaLimitInHectares = 0;
}

export class DeclarationsDataModel implements IDeclarationsDataModel {
  acceptedDeclaration = false;
  acceptedPrivacyStatement = false;
}

export class SelfAuditDataModel implements ISelfAuditDataModel {
  hasAuditEvidence = null;
}

export class ExchangeAreaDataModel implements IExchangeAreaDataModel {
  userOverrideIsExchangeAreaRequired = null;
  exchangeAreaFileReferenceNumber = '';
}

export class ExchangeAreaInformation implements IExchangeAreaInformation {
  header = '';
  questions = [];
}

export class AcknowledgementNotificationDataModel implements IAcknowledgementNotificationDataModel {
  notificationReferenceNumber = '';
}

export class RegionalEcosystemStatusDataModel implements IRegionalEcosystemStatusModel {
  messages = [];
  selectedStatusGroups = [];
  regionalEcosystemStatusIds = [];
}

export class ActivityRegionalEcosystemModel implements IActivityRegionalEcosystemModel {
  description = '';
  selectedRegionalEcosystems: ReviewNotificationRegionalEcosystemData[] = [];
}

export class RegionalEcosystem implements IRegionalEcosystem {
  regionalEcosystemId = -1;
  regionalEcosystem = '';
  categoryPurposeId = -1;
  activityCode = '';
  message = '';
  activityId = -1;
}

export class RegionalEcosystemDataModel implements IRegionalEcosystemDataModel {
  regionalEcosystemsApplicableFromLotPlan: IRegionalEcosystem[] = [];
  regionalEcosystemsAddedFromSearchResults: IRegionalEcosystem[] = [];
  regionalEcosystems: IRegionalEcosystem[] = [];
}

export class CategoryGroup implements ICategoryGroup {
  categoryCode = '';
  categoryPurposeId = -1;
  activityGroups: IActivityGroup[] = [];
}

export class ActivityGroup implements IActivityGroup {
  activityId = -1;
  activityCode = '';
  activityDescription = '';
  availableRegionalEcosystems: IRegionalEcosystem[] = [];
  selectedRegionalEcosystems: IRegionalEcosystem[] = [];
  regionalEcosystemsAddedFromSearchResults: IRegionalEcosystem[] = [];
  regionalEcosystemsApplicableFromLotPlan: IRegionalEcosystem[] = [];
  categoryPurposeId = -1;
  areRegionalEcosystemsRequired?: boolean | null;
}

export class ReviewNotificationRegionalEcosystemData implements IReviewNotificationRegionalEcosystemData {
  regionalEcosystem = '';
  addedManuallyFromSearchResult = false;
}

export class AcknowledgementsDataModel implements IAcknowledgementsDataModel {
  clearingInAForestArea = new AcknowledgementDataModel();
  koalaHabitatProtection = new AcknowledgementDataModel();
  wetTropicsQldWorldHeritageArea = new AcknowledgementDataModel();
  stateCommercialTimberRights = new AcknowledgementDataModel();
  recordKeepingAcknowledgementAccepted = true;
  questions: IAcknowledgementQuestionModel[] = [];
  acknowledgements: string[] = [];
}

export class AcknowledgementQuestionModel implements IAcknowledgementQuestionModel {
  acknowledgementQuestionId = -1;
  description = '';
}

export class AcknowledgementDataModel implements IAcknowledgementData {
  message = '';
  response = undefined;
}

export class AcknowledgementQuestion implements IAcknowledgementQuestion {
  acknowledgementQuestionId = -1;
  description = '';
}

export class ReasonModel implements IReasonModel {
  code = '';
  text = '';
}

export class ChannelDimensionsLimitsResponseModel implements IChannelDimensionsLimitsResponseModel {
  maxWidth = 0;
  maxLength = 0;
}

export class ChannelDimensionsModel implements IChannelDimensionsModel {
  width = 0;
  exchangeAreaRequired = false;
}
